// src/components/ClassLinkPanel.jsx
// 別学部の同じ授業を1つにまとめる（授業のリンク）【2026-09-17】
//
//   学部ごとに manaba のコースが分かれていて授業名も違うが、実際は同じ1つの授業。
//   いま開いている授業を「まとめ先」にして、ほかの授業をリンクする。
//   リンクすると、その授業の履修者・回・出席はこの授業へ移り、
//   名簿・出席記録・レポートはこの授業で1本になる（名簿の「所属」で学部を見分けられる）。
//   以後、その授業名で manaba の名簿を取り込んでも、自動でこの授業に入る。
//
//   いきなり変えない。「確認する」で何が動くかを見せてから「まとめる」。
import React, { useEffect, useMemo, useState } from 'react';
import { confirmDialog } from './ConfirmDialog';

export default function ClassLinkPanel({ classId, className, classes = [], token, setErr, setMsg, onChanged }) {
  const [info, setInfo]       = useState(null);   // { children: [...] }
  const [open, setOpen]       = useState(false);
  const [picked, setPicked]   = useState([]);
  const [q, setQ]             = useState('');
  const [plan, setPlan]       = useState(null);
  const [busy, setBusy]       = useState(false);

  const headers = () => ({ Authorization: 'Bearer ' + token(), 'Content-Type': 'application/json' });

  const load = async () => {
    try {
      const r = await fetch('/api/class-links/' + classId, { headers: headers() });
      const d = await r.json();
      if (d.success) {
        setInfo(d);
        if ((d.children || []).length > 0) setOpen(true);
      } else {
        setInfo({ children: [], error: d.message });
      }
    } catch (e) { setInfo({ children: [], error: e.message }); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setPicked([]); setPlan(null); setOpen(false); load(); }, [classId]);

  // 候補: 同じ年度の授業から、自分・まとめ済み・まとめ先になっている授業を除く
  const candidates = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return classes.filter(c => String(c.id) !== String(classId)
      && !c.linked_to_class_id && !(c.linked_children > 0)
      && (!needle || String(c.name).toLowerCase().includes(needle)));
  }, [classes, classId, q]);

  const toggle = (id) => {
    setPlan(null);
    setPicked(p => (p.includes(id) ? p.filter(x => x !== id) : [...p, id]));
  };

  const send = async (dryRun) => {
    setBusy(true); setErr(''); setMsg('');
    try {
      const r = await fetch('/api/class-links/' + classId, {
        method: 'POST', headers: headers(), body: JSON.stringify({ childIds: picked, dryRun }),
      });
      const d = await r.json();
      if (d.plan) setPlan(d.plan);
      if (!d.success) throw new Error(d.message || '失敗しました');
      if (!dryRun) {
        setMsg(d.message);
        setPicked([]); setPlan(null);
        await load();
        if (onChanged) onChanged();
      }
    } catch (e) { setErr(e.message); }
    finally     { setBusy(false); }
  };

  const unlink = async (child) => {
    if (!(await confirmDialog(
      `「${child.name}」のまとめを解除します。\n` +
      `その授業から来た履修者 ${child.students}人を元の授業へ戻します。\n` +
      'まとめている間に取った出席は、この授業に残ります。よろしいですか？'
    ))) return;
    setBusy(true); setErr(''); setMsg('');
    try {
      const r = await fetch('/api/class-links/child/' + child.id, { method: 'DELETE', headers: headers() });
      const d = await r.json();
      if (!d.success) throw new Error(d.message || '解除に失敗しました');
      setMsg(d.message);
      await load();
      if (onChanged) onChanged();
    } catch (e) { setErr(e.message); }
    finally     { setBusy(false); }
  };

  const children = (info && info.children) || [];

  return (
    <div className="card">
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0, fontSize: '1.05rem' }}>別学部の同じ授業をまとめる</h2>
        {children.length > 0 && (
          <span style={{ fontSize: '0.78rem', fontWeight: 700, color: '#1d4ed8', background: '#dbeafe',
                         borderRadius: 999, padding: '0.1rem 0.55rem' }}>
            {children.length}件をまとめています
          </span>
        )}
        <button type="button" className="btn-secondary" style={{ marginLeft: 'auto' }}
                onClick={() => setOpen(o => !o)}>
          {open ? '▲ 閉じる' : '▼ 開く'}
        </button>
      </div>

      {open && (
        <div style={{ marginTop: '0.7rem' }}>
          <p style={{ fontSize: '0.82rem', color: '#555', lineHeight: 1.8, margin: '0 0 0.6rem' }}>
            学部ごとに授業名が違っても、実際は同じ授業なら「{className || 'この授業'}」にまとめられます。
            まとめると、その授業の<strong>履修者・回・出席はこの授業に移り</strong>、
            名簿・出席記録・レポートがこの授業で1本になります（名簿の「所属」で学部を見分けられます）。
            以後、その授業名で manaba の名簿を取り込んでも、自動でこの授業に入ります。
          </p>
          {info && info.error && <div style={{ color: '#b91c1c', fontSize: '0.85rem' }}>{info.error}</div>}

          {children.length > 0 && (
            <table className="data-table" style={{ marginBottom: '0.8rem' }}>
              <thead><tr><th>まとめている授業</th><th style={{ textAlign: 'right' }}>この授業での履修者</th><th></th></tr></thead>
              <tbody>
                {children.map(c => (
                  <tr key={c.id}>
                    <td>
                      {c.name}
                      <div style={{ fontSize: '0.72rem', color: '#888' }}>
                        {c.academic_year ? c.academic_year + '年度' : ''}{c.term_name || ''}
                        {c.teacher_name ? ' ・ ' + c.teacher_name : ''}
                      </div>
                    </td>
                    <td style={{ textAlign: 'right' }}>{c.students}人</td>
                    <td><button className="btn-secondary" disabled={busy} onClick={() => unlink(c)}>解除</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <div style={{ fontSize: '0.85rem', fontWeight: 700, marginBottom: '0.3rem' }}>まとめる授業を選ぶ</div>
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="授業名で絞り込み"
                 style={{ padding: '0.3rem 0.5rem', minWidth: 220, marginBottom: '0.4rem' }} />
          <div style={{ maxHeight: 220, overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: 6, padding: '0.3rem 0.5rem' }}>
            {candidates.length === 0 && (
              <div style={{ color: '#888', fontSize: '0.82rem', padding: '0.3rem' }}>
                候補の授業がありません（画面上部の年度の授業から選べます）
              </div>
            )}
            {candidates.map(c => (
              <label key={c.id} style={{ display: 'flex', gap: '0.4rem', alignItems: 'center', padding: '0.2rem 0', cursor: 'pointer' }}>
                <input type="checkbox" checked={picked.includes(c.id)} onChange={() => toggle(c.id)} />
                <span>{c.name}</span>
                <span style={{ fontSize: '0.72rem', color: '#888' }}>
                  {c.term_name || ''} ・ 履修 {c.enrolled_count ?? '-'}人
                  {c.teacher_name ? ' ・ ' + c.teacher_name : ''}
                </span>
              </label>
            ))}
          </div>

          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.6rem', flexWrap: 'wrap' }}>
            <button className="btn-secondary" disabled={busy || picked.length === 0} onClick={() => send(true)}>
              {busy && !plan ? '確認中…' : `選んだ ${picked.length}件をまとめたらどうなるか確認する`}
            </button>
          </div>

          {plan && (
            <div style={{ marginTop: '0.7rem', border: '1px solid #fcd34d', background: '#fffbeb',
                          borderRadius: 8, padding: '0.7rem 0.9rem' }}>
              <div style={{ fontWeight: 700, color: '#92400e', marginBottom: '0.4rem' }}>
                「{plan.parent.name}」にまとめる（まだ変更していません）
              </div>
              <table className="data-table" style={{ background: '#fff' }}>
                <thead>
                  <tr><th>授業</th><th style={{ textAlign: 'right' }}>履修者</th><th style={{ textAlign: 'right' }}>実施回</th>
                      <th style={{ textAlign: 'right' }}>出席記録</th><th>メモ</th></tr>
                </thead>
                <tbody>
                  {plan.children.map(c => (
                    <tr key={c.id} style={c.ok ? undefined : { opacity: 0.6 }}>
                      <td>{c.name || ('#' + c.id)}</td>
                      <td style={{ textAlign: 'right' }}>{c.enrolled ?? '-'}</td>
                      <td style={{ textAlign: 'right' }}>{c.sessions ?? '-'}</td>
                      <td style={{ textAlign: 'right' }}>{c.attendance ?? '-'}</td>
                      <td style={{ fontSize: '0.8rem' }}>
                        {!c.ok && <span style={{ color: '#b91c1c' }}>まとめられません：{c.reason}</span>}
                        {c.ok && c.overlap > 0 && <div>両方に履修している学生 {c.overlap}人（1人として数えます）</div>}
                        {c.ok && c.sameDaySessions > 0 && (
                          <div>同じ日の回 {c.sameDaySessions}回は、出席をこの授業の回にまとめます</div>
                        )}
                        {c.ok && c.teacher_id !== plan.parent.teacher_id && (
                          <div style={{ color: '#b45309' }}>担当教員が違います（{c.teacher_name || '不明'}）</div>
                        )}
                        {c.ok && !c.overlap && !c.sameDaySessions && c.teacher_id === plan.parent.teacher_id && 'そのまま移します'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.6rem', alignItems: 'center', flexWrap: 'wrap' }}>
                <button className="btn-primary" disabled={busy || !plan.children.some(c => c.ok)} onClick={() => send(false)}>
                  {busy ? 'まとめています…' : 'この内容でまとめる'}
                </button>
                <button className="btn-secondary" disabled={busy} onClick={() => setPlan(null)}>やめる</button>
                <span style={{ fontSize: '0.78rem', color: '#6b7280' }}>
                  あとから「解除」で履修者を元の授業に戻せます（まとめている間の出席はこの授業に残ります）
                </span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
